import { Component, ElementRef, OnDestroy, OnInit, SecurityContext, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl, Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { ActivationDestinationService } from '../onboarding-checklist/activation-destination.service';
import { ActivationJourneyService } from '../onboarding-checklist/activation-journey.service';
import { ActivationNavigationService } from '../onboarding-checklist/activation-navigation.service';
import { ExampleEntryContext, ExampleWorkdayService } from './example-workday.service';
import {
  ExampleDailyTrendPoint,
  ExampleUsageRow,
  ExampleWorkday,
  ExampleWorkdayStepId,
} from './example-workday.model';
import { colors } from '../colors';

const MAX_TREND_Y_TICKS = 12;

interface ExampleTrendPoint {
  x: number;
  y: number;
}

interface ExampleUsageChartView {
  rankedRows: ExampleUsageRow[];
  topRows: ExampleUsageRow[];
  topBarPercents: number[];
  trendMaxHours: number;
  trendYTicks: number[];
  trendYTicksDesc: number[];
  trendAppPoints: ExampleTrendPoint[];
  trendWebPoints: ExampleTrendPoint[];
  trendAppArea: string;
  trendWebArea: string;
  trendAppLine: string;
  trendWebLine: string;
}

@Component({
  selector: 'app-example-workday',
  templateUrl: './example-workday.component.html',
  styleUrls: ['./example-workday.component.css'],
  standalone: false,
})
export class ExampleWorkdayComponent implements OnInit, OnDestroy {
  workday: ExampleWorkday;
  stepIndex = 0;
  insightsUrl: SafeResourceUrl | null = null;
  entry: ExampleEntryContext | null = null;
  readonly laterHint = 'After you connect a computer';
  /** Soft-gate tease when a locked rail item is clicked (understand/ready). */
  gatedTeaseOpen = false;
  gatedTeaseItem: string | null = null;
  readonly gatedTeaseTitle = 'After you connect a computer';
  readonly gatedTeaseBody =
    'You’ll see their screen, apps, and time here — same idea as this example.';
  readonly gatedTeasePrimary = 'Install on this computer';
  readonly gatedTeaseSecondary = 'Keep looking at the example';
  readonly previewNav = [
    { id: 'dashboard', label: 'Dashboard', icon: 'dashboard', stepId: null },
    { id: 'employees', label: 'Employees', icon: 'person', stepId: null },
    { id: 'live', label: 'Live Monitoring', icon: 'videocam', stepId: null },
    { id: 'screenshots', label: 'Screenshots', icon: 'photo_camera', stepId: null },
    { id: 'replay', label: 'Work Replay', icon: 'movie', stepId: 'replay' as ExampleWorkdayStepId },
    { id: 'teams', label: 'Teams', icon: 'groups', stepId: null },
  ] as const;
  readonly previewReportChildren = [
    { id: 'hours', label: 'Timesheet', icon: 'summarize', stepId: 'hours' as ExampleWorkdayStepId },
    { id: 'usage', label: 'App & Website Usage', icon: 'assessment', stepId: 'usage' as ExampleWorkdayStepId },
    {
      id: 'trends',
      label: 'Productivity Trends',
      icon: 'query_stats',
      stepId: 'trends' as ExampleWorkdayStepId,
    },
    { id: 'insights', label: 'Daily Insights', icon: 'mail', stepId: 'insights' as ExampleWorkdayStepId },
  ] as const;
  readonly appTypeColors = {
    app: {
      background: '#32357D20',
      text: colors.dailyUsageChart.appColor,
    },
    web: {
      background: '#2172BC20',
      text: colors.dailyUsageChart.webColor,
    },
  };
  readonly trendAppColor = colors.dailyUsageChart.appColor;
  readonly trendWebColor = colors.dailyUsageChart.webColor;
  private closed = false;
  private querySub?: Subscription;
  private usageChartView: ExampleUsageChartView | null = null;
  @ViewChild('exwPage') private pageEl?: ElementRef<HTMLElement>;

  constructor(
    private exampleWorkday: ExampleWorkdayService,
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private title: Title,
    private activationJourney: ActivationJourneyService,
    private activationDestination: ActivationDestinationService,
    private activationNavigation: ActivationNavigationService
  ) {
    this.workday = this.exampleWorkday.workday;
  }

  ngOnInit(): void {
    this.title.setTitle('Example workday · Setup - Supersee');
    const entryQuery = this.route.snapshot.queryParamMap.get('entry');
    const stepQuery = this.route.snapshot.queryParamMap.get('step');
    if (!this.exampleWorkday.ensureSessionFromRoute(entryQuery, stepQuery)) {
      this.redirectAwayFromInvalidExample();
      return;
    }
    this.entry = this.exampleWorkday.getEntryContext();
    const initialStep =
      this.exampleWorkday.parseStepId(stepQuery) ||
      this.exampleWorkday.getStoredStepId() ||
      'replay';
    this.applyStep(this.exampleWorkday.getStepIndex(initialStep), {
      syncUrl: true,
      track: true,
    });

    this.querySub = this.route.queryParamMap.subscribe((params) => {
      if (this.closed) {
        return;
      }
      const stepId = this.exampleWorkday.parseStepId(params.get('step'));
      if (!stepId) {
        return;
      }
      const idx = this.exampleWorkday.getStepIndex(stepId);
      if (idx !== this.stepIndex) {
        this.applyStep(idx, { syncUrl: false, track: true });
      }
      const entry = this.exampleWorkday.parseEntryContext(params.get('entry'));
      if (entry) {
        this.entry = entry;
      }
    });
  }

  get orientationBody(): string {
    if (this.entry === 'ready') {
      return 'Real SuperSee views on one sample day — including Trends. Walk through them, then test privately on this computer when you are ready.';
    }
    return 'Real SuperSee views on one sample day — the same loop you will use with your team.';
  }

  get identityLine(): string {
    return `${this.workday.employeeName} · ${this.workday.employeeRole} · ${this.workday.dateLabel}`;
  }

  /** Product-like usage colors for Top Usage bars. */
  private readonly usageColors = colors.BarColor;

  /** Combined apps + websites ranked by time — feeds Top Usage + Detailed Usage. */
  get rankedUsageRows(): ExampleUsageRow[] {
    return this.getUsageChartView().rankedRows;
  }

  /** Top Usage chart shows the leading rows; table keeps the full list. */
  get topUsageChartRows(): ExampleUsageRow[] {
    return this.getUsageChartView().topRows;
  }

  usageBarColor(index: number): string {
    return this.usageColors[index % this.usageColors.length];
  }

  /** Width % for horizontal Top Usage bars (relative to the longest row). */
  topUsageBarPercent(row: ExampleUsageRow): number {
    const view = this.getUsageChartView();
    const idx = view.topRows.indexOf(row);
    return idx >= 0 ? view.topBarPercents[idx] : 6;
  }

  usageTypeLabel(row: ExampleUsageRow): 'app' | 'web' {
    return row.kind === 'website' ? 'web' : 'app';
  }

  get trendYTicks(): number[] {
    return this.getUsageChartView().trendYTicks;
  }

  get trendYTicksDesc(): number[] {
    return this.getUsageChartView().trendYTicksDesc;
  }

  get trendMaxHours(): number {
    return this.getUsageChartView().trendMaxHours;
  }

  trendAreaPath(series: 'apps' | 'web'): string {
    const view = this.getUsageChartView();
    return series === 'apps' ? view.trendAppArea : view.trendWebArea;
  }

  trendLinePath(series: 'apps' | 'web'): string {
    const view = this.getUsageChartView();
    return series === 'apps' ? view.trendAppLine : view.trendWebLine;
  }

  trendPoints(series: 'apps' | 'web'): ExampleTrendPoint[] {
    const view = this.getUsageChartView();
    return series === 'apps' ? view.trendAppPoints : view.trendWebPoints;
  }

  trackByUsageName(_index: number, row: ExampleUsageRow): string {
    return row.name;
  }

  trackByTrendPoint(_index: number, point: ExampleTrendPoint): number {
    return point.x;
  }

  trackByIndex(index: number): number {
    return index;
  }

  get primaryCtaLabel(): string {
    if (this.entry === 'ready' || this.entry === 'understand') {
      return 'Install on this computer';
    }
    return this.workday.primaryCta;
  }

  get primaryCtaSupport(): string {
    if (this.entry === 'ready' || this.entry === 'understand') {
      return 'This is an example. Install SuperSee on this computer for about two minutes to see your own screen.';
    }
    return this.workday.primaryCtaSupport;
  }

  /** Soft-gate locked rail items on Look around (before private proof). */
  get softGateRailEnabled(): boolean {
    return this.entry === 'ready' || this.entry === 'understand';
  }

  ngOnDestroy(): void {
    this.querySub?.unsubscribe();
    if (!this.closed && this.exampleWorkday.isSessionActive()) {
      this.exampleWorkday.markClosed('navigate_away');
      this.closed = true;
    }
  }

  get currentStep() {
    return this.workday.steps[this.stepIndex];
  }

  get isFirst(): boolean {
    return this.stepIndex === 0;
  }

  get isLast(): boolean {
    return this.stepIndex === this.workday.steps.length - 1;
  }

  stepState(index: number): 'done' | 'current' | 'upcoming' {
    if (index < this.stepIndex) {
      return 'done';
    }
    if (index === this.stepIndex) {
      return 'current';
    }
    return 'upcoming';
  }

  isPreviewStepActive(stepId: ExampleWorkdayStepId): boolean {
    return this.currentStep?.id === stepId;
  }

  isReportsSectionActive(): boolean {
    const id = this.currentStep?.id;
    return id === 'usage' || id === 'hours' || id === 'trends' || id === 'insights';
  }

  selectStep(index: number): void {
    if (index < 0 || index >= this.workday.steps.length) {
      return;
    }
    this.applyStep(index, { syncUrl: true, track: true });
  }

  selectPreviewStep(stepId: ExampleWorkdayStepId): void {
    this.dismissGatedTease();
    this.selectStep(this.exampleWorkday.getStepIndex(stepId));
  }

  onGatedRailClick(itemId: string): void {
    if (!this.softGateRailEnabled) {
      return;
    }
    this.gatedTeaseOpen = true;
    this.gatedTeaseItem = itemId;
    this.activationJourney.trackEvent('example_rail_gated_click', {
      item: itemId,
      entry: this.entry,
    });
  }

  dismissGatedTease(): void {
    this.gatedTeaseOpen = false;
    this.gatedTeaseItem = null;
  }

  previous(): void {
    if (!this.isFirst) {
      this.applyStep(this.stepIndex - 1, { syncUrl: true, track: true });
    }
  }

  next(): void {
    if (!this.isLast) {
      this.applyStep(this.stepIndex + 1, { syncUrl: true, track: true });
    }
  }

  exitExample(): void {
    this.closed = true;
    this.exampleWorkday.exitExample();
  }

  tryOneEmployee(): void {
    this.closed = true;
    this.exampleWorkday.goTryOneEmployee();
  }

  private applyStep(
    index: number,
    opts: { syncUrl: boolean; track: boolean }
  ): void {
    if (index < 0 || index >= this.workday.steps.length) {
      return;
    }
    this.stepIndex = index;
    const stepId = this.workday.steps[index].id as ExampleWorkdayStepId;
    if (opts.track) {
      this.exampleWorkday.markStepViewed(stepId);
    }
    if (opts.syncUrl) {
      this.exampleWorkday.syncStepQuery(stepId);
    }
    if (stepId === 'insights') {
      const safe = this.sanitizer.sanitize(
        SecurityContext.URL,
        this.workday.insightsHtmlPath
      );
      this.insightsUrl = safe
        ? this.sanitizer.bypassSecurityTrustResourceUrl(safe)
        : null;
    }
    // Scroll the preview pane, not the window. Smooth window.scrollTo fights the
    // 100vh overflow lock and retriggers change detection on every animation frame.
    this.pageEl?.nativeElement.scrollTo(0, 0);
  }

  private getUsageChartView(): ExampleUsageChartView {
    if (!this.usageChartView) {
      this.usageChartView = this.buildUsageChartView();
    }
    return this.usageChartView;
  }

  private buildUsageChartView(): ExampleUsageChartView {
    const rankedRows = [...this.workday.apps, ...this.workday.websites].sort(
      (a, b) => this.durationToMinutes(b.duration) - this.durationToMinutes(a.duration)
    );
    const topRows = rankedRows.slice(0, 8);
    const maxBarMinutes = Math.max(
      ...topRows.map((item) => this.durationToMinutes(item.duration)),
      1
    );
    const topBarPercents = topRows.map((row) =>
      Math.max(6, Math.round((this.durationToMinutes(row.duration) / maxBarMinutes) * 100))
    );

    const peak = Math.max(
      ...this.workday.dailyTrend.map((p) => Math.max(p.appsHours, p.webHours)),
      1
    );
    const rawMax = Math.ceil(peak / 2) * 2;
    const trendMaxHours = Number.isFinite(rawMax) && rawMax > 0 ? rawMax : 2;
    const trendYTicks = this.buildTrendYTicks(trendMaxHours);
    const trendAppPoints = this.buildTrendPoints('apps', trendMaxHours);
    const trendWebPoints = this.buildTrendPoints('web', trendMaxHours);

    return {
      rankedRows,
      topRows,
      topBarPercents,
      trendMaxHours,
      trendYTicks,
      trendYTicksDesc: [...trendYTicks].reverse(),
      trendAppPoints,
      trendWebPoints,
      trendAppArea: this.buildTrendAreaPath(trendAppPoints),
      trendWebArea: this.buildTrendAreaPath(trendWebPoints),
      trendAppLine: this.buildTrendLinePath(trendAppPoints),
      trendWebLine: this.buildTrendLinePath(trendWebPoints),
    };
  }

  private buildTrendYTicks(maxHours: number): number[] {
    const max = Number.isFinite(maxHours) && maxHours > 0 ? maxHours : 2;
    const step = max <= 4 ? 1 : 2;
    const ticks: number[] = [];
    if (!Number.isFinite(step) || step <= 0) {
      return [0, 2];
    }
    for (let value = 0; value <= max && ticks.length < MAX_TREND_Y_TICKS; value += step) {
      ticks.push(value);
    }
    return ticks.length ? ticks : [0, 2];
  }

  private buildTrendPoints(series: 'apps' | 'web', maxHours: number): ExampleTrendPoint[] {
    const data = this.workday.dailyTrend;
    const n = data.length;
    if (!n) {
      return [];
    }
    const max = Number.isFinite(maxHours) && maxHours > 0 ? maxHours : 2;
    return data.map((point: ExampleDailyTrendPoint, index: number) => {
      const raw = series === 'apps' ? point.appsHours : point.webHours;
      const hours = Number.isFinite(raw) ? raw : 0;
      const x = n === 1 ? 50 : (index / (n - 1)) * 100;
      const y = 100 - (hours / max) * 92 - 4;
      return { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 };
    });
  }

  private buildTrendLinePath(points: ExampleTrendPoint[]): string {
    if (!points.length) {
      return '';
    }
    return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  }

  private buildTrendAreaPath(points: ExampleTrendPoint[]): string {
    if (!points.length) {
      return '';
    }
    const bottom = 100;
    const start = points[0];
    const end = points[points.length - 1];
    const line = points.map((p) => `L ${p.x} ${p.y}`).join(' ');
    return `M ${start.x} ${bottom} L ${start.x} ${start.y} ${line} L ${end.x} ${bottom} Z`;
  }

  private durationToMinutes(duration: string): number {
    const hours = Number((/(\d+)\s*h/.exec(duration) || [])[1] || 0);
    const minutes = Number((/(\d+)\s*m/.exec(duration) || [])[1] || 0);
    return hours * 60 + minutes;
  }

  private redirectAwayFromInvalidExample(): void {
    this.closed = true;
    const dest = this.activationDestination.resolve({
      state: this.activationJourney.getState(),
      requestedPath: '/setup',
    });
    void this.activationNavigation.navigateSetupUrl([dest.route], {
      queryParams: dest.queryParams || {},
      replaceUrl: true,
    });
  }
}
